
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Layout from '../../../components/docs/Layout';
import RateLimitTableWithFlagFilter from '../../../components/docs/RateLimitTable/RateLimitTableWithFlagFilter';
import { RateLimitForError } from '../../../components/docs/RateLimitFor';
import rateLimits from '../../../data/generated/rate-limits-filtered.json';
export const metadata = {
  toc: true,
  layout: 'guide',
  source: 'error',
  description: 'Guide to understanding and troubleshooting errors returned when the request is valid but has exceeded established rate limits.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const ErrorReference = makeShortcode("ErrorReference");
const Checkbox = makeShortcode("Checkbox");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Rate limit exceeded errors' subtitle='Guide to troubleshooting rate limit exceeded errors' mdxType="Header" />
    <h4 {...{
      "id": "rate-limit-table"
    }}>{`Rate limit table`}</h4>
    <p>{`Errors of type `}<inlineCode parentName="p">{`RATE_LIMIT_EXCEEDED`}</inlineCode>{` will occur when the rate limit for a
particular endpoint has been exceeded. Rate limits are based on fixed windows, not rolling periods. Default rate limit thresholds for some of
the most commonly rate-limited endpoints are shown below. Note that this table
is not an exhaustive listing of all Plaid rate limits or rate-limited
endpoints, that some customers may experience different rate limit thresholds from
those shown, and that rate limits are subject to change at any time.  `}</p>
    <p>{`In general, Plaid default rate limits are set such that using the API as
designed should typically not cause a rate limit to be encountered. If your use
case requires a higher rate limit, contact your account manager or file a
`}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
      }}>{`Support request`}</a>{`.`}</p>
    <Layout.FullWidth>
  <RateLimitTableWithFlagFilter rows={rateLimits.rows} mdxType="RateLimitTableWithFlagFilter" />
    </Layout.FullWidth>
    <h4 {...{
      "id": "accounts_limit"
    }}><strong parentName="h4">{`ACCOUNTS_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='ACCOUNTS_LIMIT' subtitle="Too many requests were made to the `/accounts/get` endpoint." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time. `}<RateLimitForError error='ACCOUNTS_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='ACCOUNTS_LIMIT' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/accounts/get`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='ACCOUNTS_LIMIT_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "accounts_balance_get_limit"
    }}><strong parentName="h4">{`ACCOUNTS_BALANCE_GET_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='ACCOUNTS_BALANCE_GET_LIMIT' subtitle="Too many requests were made to the `/accounts/balance/get` endpoint (per-client limit)." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-1"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time by a single client. `}<RateLimitForError error='ACCOUNTS_BALANCE_GET_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-1"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='ACCOUNTS_BALANCE_GET_RATE_LIMIT_EXCEEDED' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='ACCOUNTS_BALANCE_GET_RATE_LIMIT_EXCEEDED_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "auth_limit"
    }}><strong parentName="h4">{`AUTH_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='AUTH_LIMIT' subtitle="Too many requests were made to the `/auth/get` endpoint." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-2"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time. `}<RateLimitForError error='AUTH_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-2"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='AUTH_GET_RATE_LIMIT_EXCEEDED' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='AUTH_GET_RATE_LIMIT_EXCEEDED_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "balance_limit"
    }}><strong parentName="h4">{`BALANCE_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='BALANCE_LIMIT' subtitle="Too many requests were made to the `/accounts/balance/get` endpoint (per-Item limit)." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-3"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made for a single Item in a short period of time. `}<RateLimitForError error='BALANCE_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-3"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='BALANCE_GET_RATE_LIMIT_EXCEEDED' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='BALANCE_GET_RATE_LIMIT_EXCEEDED_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "credits_exhausted"
    }}><strong parentName="h4">{`CREDITS_EXHAUSTED`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='CREDITS_EXHAUSTED' subtitle="You have used up your free API usage allocation in Limited Production" server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-4"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`You ran out of free API calls for a given product in Limited Production.`}</li>
        <li parentName="ul">{`You do not yet have Production access and hit the Item creation cap in Limited Production.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-4"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='CREDITS_EXHAUSTED_1' mdxType="Checkbox">
        <p>{`Request full Production access from the
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/onboarding/"
          }}>{`Dashboard`}</a>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='CREDITS_EXHAUSTED_2' mdxType="Checkbox">
        <p>{`If you need more Limited Production API calls in order to test your use case, contact your account manager or file a ticket with
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/admin/account-administration"
          }}>{`Plaid support`}</a>{`
to request additional usage.`}</p>
      </Checkbox>
      <Checkbox checklistId='CREDITS_EXHAUSTED_3' mdxType="Checkbox">
        <p>{`Test in a different environment, such as Sandbox.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "identity_limit"
    }}><strong parentName="h4">{`IDENTITY_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='IDENTITY_LIMIT' subtitle="Too many requests were made to the `/identity/get` endpoint." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-5"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time. `}<RateLimitForError error='IDENTITY_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-5"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='IDENTITY_GET_RATE_LIMIT_EXCEEDED' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/identity/get`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='IDENTITY_GET_RATE_LIMIT_EXCEEDED_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "institutions_get_limit"
    }}><strong parentName="h4">{`INSTITUTIONS_GET_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='INSTITUTIONS_GET_LIMIT' subtitle="Too many requests were made to the `/institutions/get` endpoint." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-6"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time. `}<RateLimitForError error='INSTITUTIONS_GET_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-6"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='INSTITUTIONS_GET_LIMIT-1' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/institutions/get`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='INSTITUTIONS_GET_LIMIT-2' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "institutions_get_by_id_limit"
    }}><strong parentName="h4">{`INSTITUTIONS_GET_BY_ID_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='INSTITUTIONS_GET_BY_ID_LIMIT' subtitle="Too many requests were made to the `/institutions/get_by_id` endpoint." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-7"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time. `}<RateLimitForError error='INSTITUTIONS_GET_BY_ID_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-7"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='INSTITUTIONS_GET_BY_ID_LIMIT' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/institutions/get_by_id`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='INSTITUTIONS_GET_BY_ID_LIMIT_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "institution_rate_limit"
    }}><strong parentName="h4">{`INSTITUTION_RATE_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='INSTITUTION_RATE_LIMIT' subtitle="Too many requests were made to a given institution." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-8"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made by Plaid in a short period of time to a given institution. Because each institution has unique rate limiting behavior, Plaid cannot provide exact details of how many requests are necessary to trigger this behavior.`}</li>
        <li parentName="ul">{`This error will only trigger when calling API endpoints that request real-time data from the institution, such as `}<inlineCode parentName="li">{`/accounts/balance/get`}</inlineCode>{` or `}<inlineCode parentName="li">{`/transactions/refresh`}</inlineCode>{`.`}</li>
        <li parentName="ul">{`This institution-level limit is distinct from per-Item or per-client limits and is not applied to user-present traffic (e.g., within Link).`}</li>
        <li parentName="ul">{`In some cases, the cause of the rate limit may be another client and may not be your client application's behavior.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-8"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='INSTITUTION_RATE_LIMIT-1' mdxType="Checkbox">
        <p>{`If your client made a very large number of requests in a short time to a real-time endpoint such as `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` at a single institution, consider adding logic to spread these requests over a longer time window.`}</p>
      </Checkbox>
      <Checkbox checklistId='INSTITUTION_RATE_LIMIT-2' mdxType="Checkbox">
        <p>{`Use an exponential backoff retry algorithm to try your request again later.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "investments_holdings_get_limit"
    }}><strong parentName="h4">{`INVESTMENTS_HOLDINGS_GET_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='INVESTMENTS_HOLDINGS_GET_LIMIT' subtitle="Too many requests were made to the `/investments/holdings/get` endpoint." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-9"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time. `}<RateLimitForError error='INVESTMENTS_HOLDINGS_GET_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-9"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='INVESTMENT_HOLDINGS_GET_LIMIT' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/investments/holdings/get`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='INVESTMENT_HOLDINGS_GET_LIMIT_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "investments_limit"
    }}><strong parentName="h4">{`INVESTMENTS_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='INVESTMENTS_LIMIT' subtitle="Too many requests were made to the `/investments/transactions/get` endpoint (per-Item limit)." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-10"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time. `}<RateLimitForError error='INVESTMENTS_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-10"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='INVESTMENT_TRANSACTIONS_GET_LIMIT' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='INVESTMENT_TRANSACTIONS_GET_LIMIT_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "item_get_limit"
    }}><strong parentName="h4">{`ITEM_GET_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='ITEM_GET_LIMIT' subtitle="Too many requests were made to the `/item/get` endpoint." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-11"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time. `}<RateLimitForError error='ITEM_GET_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-11"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='ITEM_GET_RATE_LIMIT_EXCEEDED' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/item/get`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='ITEM_GET_RATE_LIMIT_EXCEEDED_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "rate_limit"
    }}><strong parentName="h4">{`RATE_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='RATE_LIMIT' subtitle="Too many requests were made." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-12"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time.`}</li>
        <li parentName="ul">{`Sandbox test credentials were used to attempt to log in to Production.
Because using these credentials in a live environment is a common
misconfiguration, they are frequently subject to rate limiting.`}</li>
        <li parentName="ul">{`A Link attempt was detected as potential attempted abuse. For example, this
may occur if a user enters their credentials incorrectly too many times in a
row.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-12"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='RATE_LIMIT_EXCEEDED' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to a Plaid
endpoint.`}</p>
      </Checkbox>
      <Checkbox checklistId='RATE_LIMIT_EXCEEDED_ENV' mdxType="Checkbox">
        <p>{`Verify that you are not using Sandbox credentials in Production.`}</p>
      </Checkbox>
      <Checkbox checklistId='RATE_LIMIT_EXCEEDED_FRAUD' mdxType="Checkbox">
        <p>{`Have your user wait 1-2 days and attempt to log in again later.`}</p>
      </Checkbox>
      <Checkbox checklistId='RATE_LIMIT_EXCEEDED_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "transactions_limit"
    }}><strong parentName="h4">{`TRANSACTIONS_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='TRANSACTIONS_LIMIT' subtitle="Too many requests were made to a transactions endpoint such as `/transactions/get` or `/transactions/refresh`." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-13"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time. `}<RateLimitForError error='TRANSACTIONS_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-13"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`Use the `}<inlineCode parentName="p">{`count`}</inlineCode>{` request parameter in the `}<inlineCode parentName="p">{`/transactions/get`}</inlineCode>{` call to increase the number of transactions retrieved per request. By default, transactions endpoints will retrieve 100 transactions at a time, but you can retrieve up to 500 at a time by setting `}<inlineCode parentName="p">{`count`}</inlineCode>{` to the desired number. The more transactions you retrieve per API call, the fewer total API calls you will need to make.`}</p>
      </Checkbox>
      <Checkbox checklistId='TRANSACTIONS_GET_RATE_LIMIT_EXCEEDED' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to a transactions endpoint.`}</p>
      </Checkbox>
      <Checkbox checklistId='TRANSACTIONS_GET_RATE_LIMIT_EXCEEDED_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "transactions_refresh_limit"
    }}><strong parentName="h4">{`TRANSACTIONS_REFRESH_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='TRANSACTIONS_REFRESH_LIMIT' subtitle="Too many requests were made to the `/transactions/refresh` endpoint." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-14"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time. `}<RateLimitForError error='TRANSACTIONS_REFRESH_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-14"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`If you are sending `}<inlineCode parentName="p">{`/transactions/refresh`}</inlineCode>{` requests based on end user behavior (such as a "refresh" button in your UI), limit the frequency at which you will send these requests or the end user can initiate them. Since `}<inlineCode parentName="p">{`/transactions/refresh`}</inlineCode>{` is billed on a per API call basis, unneeded API calls to this endpoint can lead to excess billing.`}</p>
      </Checkbox>
      <Checkbox checklistId='TRANSACTIONS_REFRESH_RATE_LIMIT_EXCEEDED' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/transactions/refresh`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='TRANSACTIONS_REFRESH_RATE_LIMIT_EXCEEDED_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "transactions_sync_limit"
    }}><strong parentName="h4">{`TRANSACTIONS_SYNC_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='TRANSACTIONS_SYNC_LIMIT' subtitle="Too many requests were made to the `/transactions/sync` endpoint." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-15"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Too many requests were made in a short period of time. `}<RateLimitForError error='TRANSACTIONS_SYNC_LIMIT' rows={rateLimits.rows} mdxType="RateLimitForError" /></li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-15"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`Use the `}<inlineCode parentName="p">{`count`}</inlineCode>{` request parameter in the `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` call to increase the number of transactions retrieved per request. By default, transactions endpoints will retrieve 100 transactions at a time, but you can retrieve up to 500 at a time by setting `}<inlineCode parentName="p">{`count`}</inlineCode>{` to the desired number. The more transactions you retrieve per API call, the fewer total API calls you will need to make.`}</p>
      </Checkbox>
      <Checkbox checklistId='TRANSACTIONS_SYNC_RATE_LIMIT_EXCEEDED' mdxType="Checkbox">
        <p>{`Check the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/logs"
          }}>{`activity log`}</a>{` in the
Dashboard to view a history of all requests made with your API keys and verify
that you are not accidentally sending an excessive number of requests to
`}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{`. If you are not already doing so, you may be able to reduce your
number of requests to `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` by specifying 500 for the `}<inlineCode parentName="p">{`count`}</inlineCode>{`
request field.`}</p>
      </Checkbox>
      <Checkbox checklistId='TRANSACTIONS_SYNC_RATE_LIMIT_EXCEEDED_AM' mdxType="Checkbox">
        <p>{`If your use case requires a higher rate limit, contact your account manager or
file a
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/product-functionality"
          }}>{`Support request`}</a>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "trial_connection_limit"
    }}><strong parentName="h4">{`TRIAL_CONNECTION_LIMIT`}</strong></h4>
    <ErrorReference type='RATE_LIMIT_EXCEEDED' error='TRIAL_CONNECTION_LIMIT' subtitle="The Plaid Trial plan connection limit has been reached." server client mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-16"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Your application is on a Plaid Trial plan and has exceeded the maximum number of allowed bank connections (Items).`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-16"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='TRIAL_CONNECTION_LIMIT-2' mdxType="Checkbox">
        <p>{`Upgrade to a paid plan from the
`}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/settings/team/plans"
          }}>{`Plans page`}</a>{` in the Dashboard to
continue adding Items.`}</p>
      </Checkbox>
    </ErrorReference>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "rate-limit-table",
        level: 4,
        title: "Rate limit table",
      },
{
        id: "accounts_limit",
        level: 4,
        title: "ACCOUNTS_LIMIT",
      },
{
        id: "accounts_balance_get_limit",
        level: 4,
        title: "ACCOUNTS_BALANCE_GET_LIMIT",
      },
{
        id: "auth_limit",
        level: 4,
        title: "AUTH_LIMIT",
      },
{
        id: "balance_limit",
        level: 4,
        title: "BALANCE_LIMIT",
      },
{
        id: "credits_exhausted",
        level: 4,
        title: "CREDITS_EXHAUSTED",
      },
{
        id: "identity_limit",
        level: 4,
        title: "IDENTITY_LIMIT",
      },
{
        id: "institutions_get_limit",
        level: 4,
        title: "INSTITUTIONS_GET_LIMIT",
      },
{
        id: "institutions_get_by_id_limit",
        level: 4,
        title: "INSTITUTIONS_GET_BY_ID_LIMIT",
      },
{
        id: "institution_rate_limit",
        level: 4,
        title: "INSTITUTION_RATE_LIMIT",
      },
{
        id: "investments_holdings_get_limit",
        level: 4,
        title: "INVESTMENTS_HOLDINGS_GET_LIMIT",
      },
{
        id: "investments_limit",
        level: 4,
        title: "INVESTMENTS_LIMIT",
      },
{
        id: "item_get_limit",
        level: 4,
        title: "ITEM_GET_LIMIT",
      },
{
        id: "rate_limit",
        level: 4,
        title: "RATE_LIMIT",
      },
{
        id: "transactions_limit",
        level: 4,
        title: "TRANSACTIONS_LIMIT",
      },
{
        id: "transactions_refresh_limit",
        level: 4,
        title: "TRANSACTIONS_REFRESH_LIMIT",
      },
{
        id: "transactions_sync_limit",
        level: 4,
        title: "TRANSACTIONS_SYNC_LIMIT",
      },
{
        id: "trial_connection_limit",
        level: 4,
        title: "TRIAL_CONNECTION_LIMIT",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
